import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Loader2, Lock, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import PageSEO from "../components/SEO";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";

/**
 * Página pública dedicada a eliminar cuenta MercaGo.
 *
 * Diseño standalone (sin header/footer de la app) para que:
 *  - Funcione bien embebida en la webview de la app móvil.
 *  - El usuario que llegue por un link compartido no se distraiga con
 *    navegación lateral.
 *
 * Reusa el endpoint ``DELETE /api/users/me`` de Fase A — ningún cambio
 * backend.
 */
export default function DeleteAccountPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  // Sobrescribe el meta robots default del `index.html` por noindex,nofollow
  // mientras esta página esté montada. ``PageSEO`` ya añade un segundo meta
  // via Helmet, pero algunos crawlers / testers de SEO solo leen el primero
  // encontrado en el DOM; con esto quedamos inequívocos.
  useEffect(() => {
    const originals = [
      ...document.querySelectorAll('meta[name="robots"]:not([data-rh])'),
    ].map((el) => ({ el, previous: el.content }));
    originals.forEach(({ el }) => {
      el.content = "noindex, nofollow";
    });
    return () => {
      originals.forEach(({ el, previous }) => {
        el.content = previous;
      });
    };
  }, []);

  const [password, setPassword] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const canSubmit = password.trim().length > 0 && confirmChecked && !deleting;

  const submitDelete = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Madrid";
      await api.delete("/users/me", {
        data: { password, confirm: true, timezone },
      });
      toast.success("Cuenta eliminada. Te enviaremos un email de confirmación.");
      logout();
      setTimeout(() => navigate("/", { replace: true }), 2000);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) {
        setDeleteError("No puedes eliminar una cuenta admin desde aquí.");
      } else if (status === 400 && typeof detail === "string") {
        setDeleteError(detail);
      } else {
        setDeleteError("No se pudo eliminar la cuenta. Inténtalo de nuevo.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div
      data-testid="delete-account-page"
      className="min-h-screen bg-white flex flex-col"
    >
      <PageSEO
        title="Eliminar cuenta — MercaGo"
        description="Elimina tu cuenta MercaGo. Esta acción es irreversible y borra todos tus datos personales."
        noindex
      />

      {/* Header minimal — solo logo, nada más. Pensado para webview móvil. */}
      <header className="border-b border-neutral-100 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="delete-account-logo-link"
            className="flex items-center gap-2 font-black text-lg"
          >
            <div className="h-7 w-7 rounded-lg bg-[#00953B] text-white grid place-items-center">
              M
            </div>
            Merca<span className="text-[#00953B]">Go</span>
          </Link>
          <Link
            to="/"
            data-testid="delete-account-home-link"
            className="text-xs text-neutral-500 hover:text-neutral-800 flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver al inicio
          </Link>
        </div>
      </header>

      <main className="flex-1 py-10 sm:py-16 px-4 sm:px-6">
        <div className="mx-auto max-w-[480px]">
          <div className="flex justify-center mb-5">
            <div className="h-16 w-16 rounded-full bg-red-100 grid place-items-center">
              <AlertTriangle
                className="h-8 w-8 text-red-600"
                strokeWidth={2.25}
              />
            </div>
          </div>

          <h1
            data-testid="delete-account-title"
            className="text-2xl sm:text-3xl font-black tracking-tight text-center text-neutral-900"
          >
            Eliminar mi cuenta MercaGo
          </h1>

          {/* --- CARGANDO estado auth --- */}
          {loading && (
            <div
              data-testid="delete-account-loading"
              className="mt-8 h-32 rounded-2xl bg-neutral-100 animate-pulse"
            />
          )}

          {/* --- NO LOGUEADO --- */}
          {!loading && !user && (
            <div
              data-testid="delete-account-anon"
              className="mt-6 rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6 text-center"
            >
              <p className="text-neutral-700">
                Para eliminar tu cuenta necesitas{" "}
                <b>iniciar sesión primero</b>.
              </p>
              <Button
                asChild
                data-testid="delete-account-login-btn"
                className="mt-5 rounded-full h-12 w-full bg-[#00953B] hover:bg-[#007e32] text-white font-semibold text-base"
              >
                <Link to="/entrar?redirect=%2Feliminar-cuenta">
                  <Lock className="h-4 w-4 mr-2" />
                  Iniciar sesión
                </Link>
              </Button>
              <p className="mt-4 text-sm text-neutral-500">
                Si no tienes cuenta MercaGo, no hay nada que eliminar.
              </p>
            </div>
          )}

          {/* --- ADMIN BLOQUEADO --- */}
          {!loading && user && isAdmin && (
            <div
              data-testid="delete-account-admin-blocked"
              className="mt-6 rounded-2xl border-2 border-amber-200 bg-amber-50 p-5 sm:p-6"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-bold">Las cuentas admin no se pueden eliminar desde aquí.</p>
                  <p className="mt-1 text-amber-800/90">
                    Contacta con soporte en{" "}
                    <a
                      href="mailto:soporte@mercago.es"
                      className="underline hover:text-amber-950"
                    >
                      soporte@mercago.es
                    </a>{" "}
                    para gestionar la eliminación.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* --- CLIENTE: FORMULARIO --- */}
          {!loading && user && !isAdmin && (
            <>
              <p className="mt-3 text-sm text-neutral-600 text-center">
                Hola, <b>{user.name}</b> ({user.email})
              </p>

              <ul
                data-testid="delete-account-bullets"
                className="mt-6 space-y-2 text-sm text-neutral-700"
              >
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                  Se eliminarán todos tus datos personales (nombre, email,
                  teléfono, direcciones).
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                  Tus pedidos y envíos anteriores quedarán anonimizados por
                  obligación contable.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                  <span>
                    <b>Esta acción es irreversible.</b>
                  </span>
                </li>
              </ul>

              <form
                data-testid="delete-account-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canSubmit) submitDelete();
                }}
                className="mt-6 rounded-2xl border-2 border-red-200 bg-red-50/40 p-5 sm:p-6 space-y-4"
              >
                <div>
                  <Label htmlFor="delete-password" className="text-sm font-semibold">
                    Introduce tu contraseña actual
                  </Label>
                  <Input
                    id="delete-password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setDeleteError(null);
                      setPassword(e.target.value);
                    }}
                    data-testid="delete-account-password"
                    autoComplete="current-password"
                    placeholder="Tu contraseña"
                    className="mt-1 h-12 bg-white"
                  />
                </div>

                <label
                  htmlFor="delete-confirm"
                  className="flex items-start gap-3 rounded-xl border border-red-200 bg-white p-3 cursor-pointer"
                >
                  <Checkbox
                    id="delete-confirm"
                    checked={confirmChecked}
                    onCheckedChange={(v) => setConfirmChecked(!!v)}
                    data-testid="delete-account-confirm-check"
                    className="mt-0.5"
                  />
                  <span className="text-sm text-neutral-800 leading-snug">
                    Entiendo que esta acción es irreversible y perderé acceso a
                    mis pedidos pasados.
                  </span>
                </label>

                {deleteError && (
                  <div
                    data-testid="delete-account-error"
                    className="rounded-lg bg-white border border-red-300 px-3 py-2 text-sm text-red-700"
                  >
                    {deleteError}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="delete-account-submit"
                  className="rounded-full h-12 w-full bg-red-600 hover:bg-red-700 text-white font-semibold text-base disabled:opacity-60"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Eliminando…
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar mi cuenta definitivamente
                    </>
                  )}
                </Button>

                <p className="text-xs text-neutral-500 text-center">
                  Si cambias de opinión, simplemente cierra esta página.
                </p>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
